import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, BookOpen } from "lucide-react";
import RoboticsShell from "./RoboticsShell";

/**
 * RoboticsHeritage — /robotics/heritage.
 *
 * Two cards, one per flagship drama. Each fetches its own manifest.json
 * to display the poster image + title + hook. Click launches the player.
 */
const DRAMAS = [
  {
    slug: "arthur_c_clarke",
    label: "BIOGRAPHY",
  },
  {
    slug: "clarkes_orbit",
    label: "PHYSICS",
  },
];

function DramaCard({ slug, label }) {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch(`/robotics/dramas/${slug}/manifest.json`, { cache: "no-store" })
      .then((r) => r.ok ? r.json() : null)
      .then((m) => { if (alive) setMeta(m); })
      .catch(() => {});
    return () => { alive = false; };
  }, [slug]);

  return (
    <Link to={`/robotics/heritage/${slug}`} className="rbx-drama-card"
          data-testid={`rbx-drama-card-${slug}`}>
      <div className="rbx-drama-card__poster">
        {meta && meta.poster_url ? (
          <img src={meta.poster_url} alt={meta.title || slug}
               loading="lazy"
               data-testid={`rbx-drama-poster-${slug}`} />
        ) : (
          <div className="rbx-drama-card__poster-fallback" />
        )}
        <div className="rbx-drama-card__play" aria-hidden="true">
          <Play size={22} />
        </div>
        <div className="rbx-drama-card__ribbon">{label}</div>
      </div>
      <div className="rbx-drama-card__body">
        <h3 className="rbx-drama-card__title" data-testid={`rbx-drama-title-${slug}`}>
          {meta ? (meta.title || meta.name) : "Loading…"}
        </h3>
        <p className="rbx-drama-card__hook" data-testid={`rbx-drama-hook-${slug}`}>
          {meta ? meta.hook : "\u00a0"}
        </p>
        <div className="rbx-drama-card__meta">
          {meta && meta.total_duration_seconds
            ? `${Math.round(meta.total_duration_seconds / 60)} min · ${meta.scenes?.length || 5} scenes · warm narration + ambient sound`
            : "\u00a0"}
        </div>
      </div>
    </Link>
  );
}

export default function RoboticsHeritage() {
  return (
    <RoboticsShell soft>
      {/* FutureTec Robotics brand hero (banner crop — satellite arc from
          upper portion of the shared brand image). */}
      <div className="rbx-heritage-banner" data-testid="rbx-heritage-banner" aria-hidden="true">
        <picture>
          <source type="image/webp" srcSet="/robotics/brand/hero_kids_workbench.webp" />
          <img
            src="/robotics/brand/hero_kids_workbench.jpg"
            alt=""
            className="rbx-heritage-banner__img"
            loading="lazy"
            data-testid="rbx-heritage-banner-img"
          />
        </picture>
        <div className="rbx-heritage-banner__fade" />
      </div>

      <main className="rbx-page rbx-heritage-page" data-testid="rbx-heritage-page">
        <header className="rbx-heritage-page__header">
          <div className="rbx-heritage-page__eyebrow" data-testid="rbx-heritage-eyebrow">
            FTG · ROBOTICS ACADEMY
          </div>
          <h1 className="rbx-heritage-page__title" data-testid="rbx-heritage-title">
            The Clarke Legacy
          </h1>
          <p className="rbx-heritage-page__lede" data-testid="rbx-heritage-lede">
            Two short cinematic audio dramas about Sir Arthur C. Clarke — the
            boy from a Somerset hay field who wrote a single page of physics
            in 1945 that quietly, one satellite at a time, put the whole
            world online. Warm hand-painted scenes, gentle narration, and
            ambient sound for young curious minds — and for the parents who
            grew up on his books.
          </p>
        </header>

        <div className="rbx-heritage-page__grid" data-testid="rbx-heritage-grid">
          {DRAMAS.map((d) => (
            <DramaCard key={d.slug} slug={d.slug} label={d.label} />
          ))}
        </div>

        <aside className="rbx-heritage-page__aside" data-testid="rbx-heritage-aside">
          <BookOpen size={16} />
          <div>
            Sir Arthur personally supported the team that organised the
            first LEGO Robotics Olympiad in Sri Lanka, in 2004. This strand
            of dramas is a small thank-you.
          </div>
        </aside>
      </main>
    </RoboticsShell>
  );
}
